import { Button } from "../components/Button"
import { Title } from "../components/Title"
import { Booking, getBookingsErrorType, getBookingsByMpanSuccessType, getSiteSuccessType } from "../../../APIDocs/apiTypes"
import { Tag } from "../components/Tag"
import { useMemo, useState } from "react"
import { Typography } from "../components/Typography"
import { Modal } from "../components/Modal"
import { ListItem } from "../components/ListItem"
import { Logger } from "../Logger"
import { List } from "../components/List"
import { contractTypeCodes, RestTypes, useJbpQuery } from "../hooks/useJbpQuery"
import { JobHistoryLoading } from "./loadingElements/JobHistoryLoading";
import { SiteTypeUpdateModal } from "./SiteTypeUpdateModal"
import { useCanAccess, useIsInternalUser } from "../hooks/useUserPermissions"
import { useModel } from "../hooks/useModel"
import { useNav } from "../hooks/useNav"
import { useNavigate } from "react-router-dom";
import { useFeedbackColourForBookingStatus } from "../hooks/useFeedbackColour"

export interface props {
    className?: string,
    mpan: string,
    serviceLine: string,
    dataErrorOccurred: boolean,
    siteData: getSiteSuccessType | undefined
}

export const JobHistory = (props: props) => {
    const model = useModel();
    const nav = useNavigate();
    const isInternalUser = useIsInternalUser();
    const siteAssociatedMpidIsSet = (): boolean => {
        const siteAssociatedMpid = sessionStorage.getItem("userMPID");
        return siteAssociatedMpid != null && siteAssociatedMpid != "" && siteAssociatedMpid.indexOf(",") < 0;
    }

    if(!siteAssociatedMpidIsSet()) {
        if(props.siteData != undefined && props.siteData.MPID != undefined && props.siteData.MPID != "") {
            sessionStorage.setItem("userMPID", props.siteData.MPID);
        }
    }

    const now = new Date();
    const todayMinMillis = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0).getTime();
    const todayMaxMillis = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 0).getTime();

    const [bookingDataErrorOccurred, setBookingDataErrorOccurred] = useState(false)
    const [showSiteTypeConfirmationModal, setShowSiteTypeConfirmationModal] = useState(false);
    const [showNoActiveMopModal, setShowNoActiveMopModal] = useState(false);
    const [noActiveMopModelClosedByUser, setNoActiveMopModelClosedByUser] = useState(false);
    const bookable = model.Get("viewModel.fetchedData.bookable") as boolean | undefined;
    const activeMopForSite = props.siteData?.ActiveMopAppointment;
    const selectedContractType = model.Get("model.selectedContractType") as string | undefined;
    const contractId = model.Get("model.contractId") as string | undefined;

    if(showNoActiveMopModal == false && activeMopForSite === false && isInternalUser && noActiveMopModelClosedByUser === false) {
        setShowNoActiveMopModal(true);
    }

    const onBookingDataError = (err: getBookingsErrorType) => {
        Logger.Log(`Data found to not exist -> ${err?.msg}`)
        setBookingDataErrorOccurred(true);
    }

    const { data, isLoading } = useJbpQuery<getBookingsByMpanSuccessType, getBookingsErrorType>(
        RestTypes.Get,
        undefined,
        [`get-booking-list-${props.mpan}-${props.serviceLine}`],
        `jbp/${props.mpan}/booking`,
        true,
        props.serviceLine,
        undefined,
        onBookingDataError,
        120000,
        120000
    )

    const [displayFutureJobs, setDisplayFutureJobs] = useState(false);
    const [displayPastJobs, setDisplayPastJobs] = useState(false);
    const [displayTodaysJobs, setDisplayTodaysJobs] = useState(false);
    const [displayScheduledJobs, setDisplayScheduledJobs] = useState(false);
    const [displayAbortedJobs, setDisplayAbortedJobs] = useState(false);
    const [displayCancelledJobs, setDisplayCancelledJobs] = useState(false);
    const [displayCompletedJobs, setDisplayCompletedJobs] = useState(false);

    const [futureJobs, setFutureJobs] = useState<Booking[]>([]);

    const shouldDisplayBooking = (booking: Booking) => {

        const timeBoundFilters = [displayPastJobs, displayTodaysJobs, displayFutureJobs];
        const statusBoundFilters = [displayScheduledJobs, displayCompletedJobs, displayAbortedJobs, displayCancelledJobs];

        const ignoreTimeBoundFilters = timeBoundFilters.every(v => v === false);
        const ignoreStatusFilters = statusBoundFilters.every(v => v === false);

        if(ignoreStatusFilters && ignoreTimeBoundFilters) return true;

        if(ignoreStatusFilters === false) {
            switch(booking.Status) {
                case "Aborted":
                    if(displayAbortedJobs === false) return false;
                    break;
                case "Cancelled":
                    if(displayCancelledJobs === false) return false;
                    break;
                case "Scheduled":
                    if(displayScheduledJobs === false) return false;
                    break;
                case "Completed":
                    if(displayCompletedJobs === false) return false;
                    break;
                default:
                    Logger.Error(`Unexpected job status: ${booking.Status} when processing shouldDisplayBooking`)
            }
        }

        if(booking.StartDate === undefined) {
            return false;
        }

        if(ignoreTimeBoundFilters === false) {
            if(booking.StartDate >= todayMinMillis && booking.StartDate <= todayMaxMillis && displayTodaysJobs === false) {
                return false;
            }
            else if(booking.StartDate < todayMinMillis && displayPastJobs === false) {
                return false;
            }
            else if(booking.StartDate > todayMaxMillis && displayFutureJobs === false) {
                return false;
            }
        }

        return true;
    }

    const getFutureJobs = (): Booking[] => {
        return (data ?? []).filter((booking: Booking) => booking.StartDate != undefined && booking.StartDate > todayMaxMillis && booking.Status === "Scheduled")
    }

    const bookJobOnClick = () => {
        const futureJobs = getFutureJobs();

        if(futureJobs.length > 0) {
            setFutureJobs(futureJobs);
        } else {
            setShowSiteTypeConfirmationModal(true);
        }
    }

    const scheduledJobWarningCancelClicked = () => {
        setFutureJobs([]);
    }

    const onJobClicked = (booking: Booking) => {
        const serviceLine = Object.entries(contractTypeCodes).find(([, value]) => value === booking.ContractTypeId);
        if(serviceLine == undefined) return; // this should never happen
        useNav(`/job/${booking.BookingId}/${props.mpan}/${serviceLine[0]}/${booking.ContractId}`, nav, {
            state: { activeMopAppointment: activeMopForSite, bookingData: booking }
        });
    }

    const navToSelectJobType = () => {
        useNav(`/bookJob/siteJobType/${props.mpan}/${props.serviceLine}`, nav);
    }

    const confirmFutureJob = () => {
        setFutureJobs([]);
        setShowSiteTypeConfirmationModal(true)
    }

    const closeNoActiveMopModel = () => {
        setShowNoActiveMopModal(false);
        setNoActiveMopModelClosedByUser(true);
    }

    const title = "Job History:"

    const { jobsForContractOrServiceLine, otherBookings } = useMemo(() => {
        const newJobsForContractOrServiceLine: Booking[] = [];
        const newOtherBookings: Booking[] = [];
        if(!data || data.length === 0) {
            return { jobsForContractOrServiceLine: newJobsForContractOrServiceLine, otherBookings: newOtherBookings };
        }
        for(const booking of data) {
            if(!shouldDisplayBooking(booking)) continue;
            if((contractId == undefined || booking.ContractId == contractId) && booking.ContractTypeId == selectedContractType) {
                newJobsForContractOrServiceLine.push(booking);
            } else {
                newOtherBookings.push(booking);
            }
        }
        return { jobsForContractOrServiceLine: newJobsForContractOrServiceLine, otherBookings: newOtherBookings };
    }, [data, selectedContractType, contractId, displayFutureJobs, displayPastJobs, displayTodaysJobs, displayScheduledJobs, displayAbortedJobs, displayCancelledJobs, displayCompletedJobs]);

    const renderHistory = useMemo(() =>
        !props.dataErrorOccurred && !bookingDataErrorOccurred && !isLoading,
        [props.dataErrorOccurred, bookingDataErrorOccurred, isLoading]);

    const renderHistoryListItem = (booking: Booking, index: number) => {
        const feedbackColour = useFeedbackColourForBookingStatus(booking.Status);
        const cells = isInternalUser ? [booking.JobTypeDesc, `${booking.ContractTypeDesc} - ${booking.CustomerGroupName}`] : [booking.JobTypeDesc];
        return <ListItem key={index} title="Scheduled Date:" subTitle={booking.ScheduledDate} cells={cells} feedbackColour={feedbackColour} tagText={booking.Status} onClick={() => onJobClicked(booking)} />
    }

    return (
        <div className={`${props.className} pl-5 pr-10`}>
            <Title text={title} className="mt-7 inline-block" />
            <Button primary={true} text="Book Job" className="float-right mt-7" disabled={bookingDataErrorOccurred || isLoading || props.dataErrorOccurred || useCanAccess('canBook') === false || bookable === false || activeMopForSite == false} icon={true} onClick={bookJobOnClick} />
            {props.dataErrorOccurred && <span></span>}
            {!props.dataErrorOccurred && isLoading &&
                <div>
                    <JobHistoryLoading className={props.className}></JobHistoryLoading>
                </div>
            }
            {bookingDataErrorOccurred && !isLoading &&
                <span>
                    <Typography content="An error has occurred fetching the job history for this site. Please contact support" />
                </span>
            }
            {renderHistory && (
                <div>
                    <Tag title="Future Jobs" initiallySelected={displayFutureJobs} onClick={(value) => { setDisplayFutureJobs(value) }} />
                    <Tag title="Past Jobs" initiallySelected={displayPastJobs} onClick={(value) => { setDisplayPastJobs(value) }} />
                    <Tag title="Today's Jobs" initiallySelected={displayTodaysJobs} onClick={(value) => { setDisplayTodaysJobs(value) }} />
                    <Tag title="Scheduled" initiallySelected={displayScheduledJobs} onClick={(value) => { setDisplayScheduledJobs(value) }} />
                    <Tag title="Aborted" initiallySelected={displayAbortedJobs} onClick={(value) => { setDisplayAbortedJobs(value) }} />
                    <Tag title="Cancelled" initiallySelected={displayCancelledJobs} onClick={(value) => { setDisplayCancelledJobs(value) }} />
                    <Tag title="Completed" initiallySelected={displayCompletedJobs} onClick={(value) => { setDisplayCompletedJobs(value) }} />
                </div>
            )}
            {renderHistory && (jobsForContractOrServiceLine.length > 0 ?
                <div>
                    <List title={title} data={jobsForContractOrServiceLine} className="max-h-[50vh]">
                        {
                            jobsForContractOrServiceLine.map<JSX.Element>(renderHistoryListItem)
                        }
                    </List>
                </div>
                : <span>
                    {[displayScheduledJobs, displayCompletedJobs, displayAbortedJobs, displayCancelledJobs, displayPastJobs, displayTodaysJobs, displayFutureJobs].some(Boolean) ?
                        <Typography content="There is no job history related to the selected filters" />
                        : <Typography content="There is no job history available to view. Once a job has been booked, it will be displayed here" />}
                </span>)}
            {renderHistory && isInternalUser && otherBookings.length > 0 ?
                <div className="mb-6">
                    <Title text="Other Job History:" className="mt-7 inline-block" />
                    <List title={title} data={otherBookings} className="max-h-[50vh]">
                        {
                            otherBookings.map<JSX.Element>(renderHistoryListItem)
                        }
                    </List>
                </div> : <span>
                    {isInternalUser && <Typography content="There is no job history related to other contracts/service lines" />}
                </span>}

            {futureJobs.length > 0 ?
                <Modal title="A booking already exists" className="w-2/5">
                    <Typography content={`Are you sure you wish to book the job? The following booking(s) already exists:`} className="mb-3" />
                    <>
                        {futureJobs.map<JSX.Element>((futureJob: Booking, index: number) => {
                            const customerInfo = isInternalUser ? ` (${futureJob.ContractTypeDesc} - ${futureJob.CustomerGroupName})` : "";
                            return <Typography content={`- ${futureJob.JobTypeDesc} scheduled for ${futureJob.ScheduledDate}${customerInfo}`} key={index} />
                        })}
                    </>
                    <div className="float-right">
                        <Button primary={false} text="Cancel" className="mr-4 mt-5" disabled={false} onClick={scheduledJobWarningCancelClicked} />
                        <Button primary={false} text="Ok" disabled={false} onClick={() => { confirmFutureJob() }} />
                    </div>
                </Modal>
                :
                <span></span>
            }

            {showSiteTypeConfirmationModal ?
                <SiteTypeUpdateModal mpan={props.mpan} serviceLine={props.serviceLine} closeModalFunction={setShowSiteTypeConfirmationModal}></SiteTypeUpdateModal>
                :
                <span></span>
            }

            {showNoActiveMopModal ?
                <Modal title="No active MOP appointment" className="w-1/3">
                    <>
                        {props.siteData?.NoActiveMopAppointmentReason === 'NO_MATCHING_MPID' && <Typography content="A MOP Appointment has been identified from an MPID which is not a valid MPID for this customer. You can view site details and history, however you will not be able to book a job." className="mb-3" />}
                        {props.siteData?.NoActiveMopAppointmentReason === 'NO_ACTIVE_APPOINTMENT' && <Typography content="There is no active MOP for this site. You can view site details and history, however you will not be able to book a job." className="mb-3" />}
                        {props.siteData?.NoActiveMopAppointmentReason === 'INVALID_HOST_MPID' && <Typography content="There is an active appointment for this site but is associated with a different service line. You can still view site details and history, however you will not be able to book a job" className="mb-3" />}
                    </>
                    <div className="float-right">
                        <Button primary={false} text="Ok" disabled={false} onClick={closeNoActiveMopModel} />
                    </div>
                </Modal>
                :
                <span></span>
            }
        </div>
    )
}
